import './DetailContent.css';

import clsx from 'clsx';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useDetailScrollbarDetection } from './hooks/useDetailScrollbarDetection';
import { useDetailColumnLayout } from './hooks/useDetailColumnLayout';
import { getDetailColumnTemplate } from './utils/getDetailColumnTemplate';
import { getDetailItems } from './utils/getDetailItems';
import { useDatasetData } from '../../../api/hooks/useDatasetData';
import ApiError from '../../ApiError/ApiError';
import { useDebouncedEffect } from '../../../hooks/useDebouncedEffect';
import { getDetailParamsString } from '../../../utils/apiRequests/getDetailParamString';
import { getDetailFilterObject } from '../../../utils/apiRequests/getDetailFilterObject';
import { getDetailItemsParamArray } from '../../../utils/apiRequests/getDetailItemsParamArray';
import { DatasetDataApiRequestData } from '../../../api/types/types';
import { useUserState } from '../../../hooks/context/useUserState';
import { GRID_DATA_LIMIT } from '../../../constants';
import { apiRequest } from '../../../api/utils/apiRequest';
import { resolveTemplate } from '../../../utils/json/resolveTemplate';
import { useTranslate } from '../../../hooks/context/useTranslate';
import { useResponseActionsContext } from '../../../hooks/context/useResponseActionsContext';
import EmptyMessage from '../Grid/components/EmptyMessage/EmptyMessage';
import SeptimLoadingSpinner from '../SeptimLoadingSpinner/SeptimLoadingSpinner';
import { getDataFromDatasetRequest } from '../../../api/utils/getDataFromDatasetRequest';
import DetailValue from './components/DetailValue/DetailValue';
import Tooltip from '../Tooltip/Tooltip';
import { alignTooltipToPointer } from './utils/alignTooltipToPointer';
import { Dataset } from '../../../types/types';

interface DetailContentProps {
  row: any;
  dataset: Dataset;
  detailColumns: any;
  onLoadingChange?: (loading: boolean) => void;
  // Detail page tile only: scroll with the page instead of trapping the wheel
  disableInnerScroll?: boolean;
}

const DetailContent = ({
  row,
  dataset,
  detailColumns,
  onLoadingChange,
  disableInnerScroll = false,
}: DetailContentProps) => {
  const [data, setData] = useState<any>(null);
  // Tracks whether a fetch attempt has started; prevents empty state flash before first fetch
  const [hasFetched, setHasFetched] = useState<boolean>(false);

  const detailRef = useRef<HTMLDivElement>(null);

  // Scopes the shared detail tooltip so multiple detail panels don't cross-bind.
  const detailTooltipId: string = useId();

  // No inner scrollbar -> wheel must chain to the parent grid instead of being trapped
  const hasScrollbar = useDetailScrollbarDetection(detailRef, data);

  const items = useMemo(
    () => getDetailItems(data, detailColumns),
    [detailColumns, data]
  );
  const layout = useDetailColumnLayout(detailRef, data);
  const gridTemplateColumns = useMemo(
    () =>
      getDetailColumnTemplate(
        items.map((item) => item.wide),
        layout.count,
        layout.narrow,
        layout.wide
      ),
    [items, layout.count, layout.narrow, layout.wide]
  );
  const { t } = useTranslate();
  const {
    state: { token },
  } = useUserState();
  const { getResponseActionHandler } = useResponseActionsContext();
  const {
    data: detailData,
    fetch: fetchData,
    detailedFetch: detailedFetchData,
    error: dataError,
    isLoading: dataLoading,
  } = useDatasetData();

  // Fetch row detail data
  useDebouncedEffect(
    async () => {
      // Mark fetch as started so the empty state is not shown prematurely
      setHasFetched(true);
      const idItem = dataset.idItem;

      if (idItem && row[idItem]) {
        const items = getDetailItemsParamArray(dataset);
        const filter = getDetailFilterObject(dataset, row[idItem]);
        const requestData: DatasetDataApiRequestData = {
          datasetId: dataset.datasetId,
          sessionId: token || '',
          // "" declares Query Cache support — the header is sent empty
          cachedQueryId: '',
          offset: 0,
          limit: GRID_DATA_LIMIT,
          items: items,
          sort: [],
          filters: filter,
        };

        // String api method request flow
        if (typeof dataset.apiMethod === 'string') {
          const { apiUrl } = await resolveTemplate(
            { apiUrl: dataset.apiMethod },
            requestData
          );

          const paramsString = getDetailParamsString(dataset, row[idItem]);

          fetchData(`${apiUrl}${paramsString}`, dataset);
          return;
        }

        // Object api method request flow
        apiRequest(
          dataset.apiMethod,
          requestData,
          (apiMethod) =>
            getDataFromDatasetRequest(() =>
              detailedFetchData(apiMethod, dataset)
            ),
          getResponseActionHandler
        );
      } else {
        setData(row);
      }
    },
    [row, token],
    500
  );

  // Clear stale data immediately on row change so previous row is never shown during load
  useEffect(() => {
    setHasFetched(false);
    setData(null);
  }, [row]);

  useEffect(() => {
    if (
      detailData &&
      detailData.datasetId === dataset.datasetId &&
      detailData.data[0]
    ) {
      setData(detailData.data[0]);
    }
  }, [detailData]);

  const errorToShow =
    dataError && dataError.datasetId === dataset.datasetId ? dataError : null;

  // Cover the pre-fetch window (before the debounce fires) to avoid empty state flash
  const isLoading = !errorToShow && (!hasFetched || dataLoading);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  if (errorToShow) {
    return <ApiError error={errorToShow.error} />;
  }

  if (isLoading) {
    return <SeptimLoadingSpinner className="slave-tabs-loading-spinner" />;
  }

  if (!data) {
    // Show empty state when no detail data is available
    return (
      <div className="detail-no-data">
        <EmptyMessage message={t('noDataAvailable')} />
      </div>
    );
  }

  return (
    <div
      ref={detailRef}
      className={clsx(
        'detail',
        disableInnerScroll && 'detail-static',
        !disableInnerScroll && !hasScrollbar && 'detail-scroll-chain',
        detailTooltipId
      )}
      style={{ gridTemplateColumns }}
    >
      {items.map(({ column, value }) => (
        <div key={column.name} className="detail-param">
          <span>{column.label}</span>
          <DetailValue value={value} />
        </div>
      ))}
      <Tooltip
        target={`.${detailTooltipId} .detail-value`}
        mouseTrack
        className="detail-tooltip"
        onBeforeShow={alignTooltipToPointer}
      />
    </div>
  );
};

export default DetailContent;
